import { useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"
import { useFetchAllBrandsQuery } from "../../redux/queries"
import { useAppDispatch } from "../../common/hooks"
import { useConfigMutationFeedback } from "./use-config-mutation-feedback"
import { configBackUrl } from "./utils"

interface MutationResult {
  isSuccess: boolean
  isError: boolean
  data?: { status?: string }
  reset: () => void
}

interface UseConfigEditPageOptions {
  tabs: { id: string; label: string }[]
  tab: string
  label: string
  saveResult: MutationResult
  deleteResult?: MutationResult
  brandIdField?: string
}

export const useConfigEditPage = <T>({
  tabs,
  tab: tabSlug,
  label,
  saveResult,
  deleteResult,
  brandIdField = "brand_id"
}: UseConfigEditPageOptions) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isCreate = id === "new"
  const [tab, setTab] = useState(tabs[0].id)

  const { data: brands } = useFetchAllBrandsQuery({ limit: 999 })

  const formRef = useRef<FormikProps<T>>(null)

  const getBrandId = () => {
    const vals = formRef.current?.values as Record<string, any> | undefined
    return vals?.[brandIdField] as string | undefined
  }

  useConfigMutationFeedback({
    label,
    isCreate,
    saveResult,
    deleteResult,
    navigate,
    backUrl: () => configBackUrl(tabSlug, getBrandId()),
    dispatch
  })

  const setFormField = (name: string, val: any) => {
    formRef.current?.setFieldValue(name, val)
  }

  const navigateBack = (brandId?: string) =>
    navigate(configBackUrl(tabSlug, brandId))

  return {
    id,
    isCreate,
    formRef,
    setFormField,
    tab,
    setTab,
    brands,
    navigate,
    dispatch,
    navigateBack
  }
}
